/**
 * @file Suspense à deux temps pour le chargement initial d'un panel.
 * @module packages.common.components.loading-screen.boot-suspense
 *
 * Résout l'empilement de loaders au refresh (splash de session, puis barre
 * de chunk, puis loader de page) en distinguant deux moments :
 * - Premier chargement : le fallback est le SplashScreen plein écran — il
 *   prolonge visuellement celui du SessionProvider, l'utilisateur ne voit
 *   qu'UN seul écran de chargement du refresh jusqu'à la première page.
 * - Navigations suivantes : le layout est déjà en place, le fallback est un
 *   ContentLoader discret dans la zone de contenu (délai anti-flash inclus).
 *
 * Le drapeau « première page rendue » vit dans une ref du composant : il se
 * réinitialise naturellement à chaque refresh, sans état module partagé.
 */

import {Suspense, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import SplashScreen from './splash-screen';
import ContentLoader from './content-loader';

/**
 * Pose le drapeau « première page rendue ». Rendu en frère du contenu du
 * Suspense : son effet ne s'exécute que lorsque le premier chunk est résolu.
 *
 * @param {Object} props - Props du composant
 * @param {Object} props.bootedRef - Ref partagée du drapeau
 * @return {null} Aucun rendu
 */
function BootMark({bootedRef}) {
  useEffect(() => {
    bootedRef.current = true;
  }, [bootedRef]);
  return null;
}

BootMark.propTypes = {
  bootedRef: PropTypes.object.isRequired,
};

/**
 * Fallback qui choisit son visuel au moment où la suspension survient
 * (l'élément fallback est monté à chaque suspension : la ref est lue au
 * bon moment, pas au rendu du parent).
 *
 * @param {Object} props - Props du composant
 * @param {Object} props.bootedRef - Ref partagée du drapeau
 * @return {JSX.Element} Splash plein écran ou loader de contenu
 */
function BootAwareFallback({bootedRef}) {
  if (!bootedRef.current) {
    // Le Box plein écran est rendu de façon synchrone : il masque le layout
    // dès la première frame (le SplashScreen, lui, attend son premier effet
    // à cause de sa garde `mounted`). position fixed : couvre tout le
    // viewport (header et nav compris) pour une continuité parfaite avec le
    // splash de session qui le précède.
    return (
      <Box sx={{position: 'fixed', inset: 0, zIndex: 9998, bgcolor: 'background.default'}}>
        <SplashScreen sx={{position: 'fixed', top: 0, left: 0}} />
      </Box>
    );
  }
  return <ContentLoader />;
}

BootAwareFallback.propTypes = {
  bootedRef: PropTypes.object.isRequired,
};

/**
 * Suspense dont le fallback prolonge le splash au premier chargement, puis
 * devient un loader de contenu discret pour les navigations.
 *
 * @param {Object} props - Props du composant
 * @param {Object} props.children - Contenu (routes lazy)
 * @return {JSX.Element} Le Suspense configuré
 */
export default function BootSuspense({children}) {
  const bootedRef = useRef(false);

  return (
    <Suspense fallback={<BootAwareFallback bootedRef={bootedRef} />}>
      <BootMark bootedRef={bootedRef} />
      {children}
    </Suspense>
  );
}

BootSuspense.propTypes = {
  children: PropTypes.node,
};
